import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { projectCampaignsQueryOptions } from '@/entities/media-campaign';
import { HtmlWidgetFrame } from '@/features/html-widget';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import { useGenerateDashboard } from '@/features/campaign-dashboard';

/**
 * Pick a campaign, generate a dashboard. The media-delivery agent reads the campaign's delivery
 * and returns one HTML widget, rendered here inline rather than in the chat side panel.
 */
export function Dashboard() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const projectId = project.platformProjectId;

  const campaigns = useQuery(projectCampaignsQueryOptions(projectId));
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const { generate, widget, status, error } = useGenerateDashboard();

  const options = campaigns.data ?? [];
  const canGenerate = campaignId !== null && status !== 'running';

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex items-end gap-sm">
        <div className="flex min-w-0 flex-1 flex-col gap-xs">
          <label className="type-label-md text-muted-foreground" htmlFor="dashboard-campaign">
            {t('dashboard.campaign')}
          </label>
          <Select value={campaignId ?? ''} onValueChange={setCampaignId}>
            <SelectTrigger id="dashboard-campaign" aria-label={t('dashboard.campaign')}>
              <SelectValue placeholder={t('dashboard.chooseACampaign')} />
            </SelectTrigger>
            <SelectContent>
              {options.map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button disabled={!canGenerate} onClick={() => campaignId && void generate(campaignId)}>
          {t('dashboard.generateDashboard')}
        </Button>
      </div>

      {campaigns.isSuccess && options.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('dashboard.noCampaignsLinked')}</p>
      )}

      {status === 'running' && <Skeleton className="h-96 w-full" />}

      {status === 'empty' && (
        <p className="text-sm text-muted-foreground">{t('dashboard.noDeliveryForCampaign')}</p>
      )}

      {status === 'error' && (
        <p className="text-sm text-destructive">{error ?? t('dashboard.couldNotGenerate')}</p>
      )}

      {status === 'ready' && widget && (
        <div className="overflow-hidden rounded-lg border border-border-light">
          <HtmlWidgetFrame widget={widget} />
        </div>
      )}
    </div>
  );
}
